import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import styled from "@emotion/styled";
import { Chip, Grid, ListItem, Typography, TextField, Checkbox, Popper, Button, Box } from "@mzc-pdc/ui";
import { Autocomplete as MuiAutocomplete } from "@mzc-pdc/ui";
import { IconAvata, IconErrorCircle, IconWarningTriangle } from "@mzc-cloudplex/icons";
import useIntersection from "@hooks/useIntersection";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { autocompleteClasses } from "@mui/material/Autocomplete";
import theme from "@styles/theme";
import { RandomColoredAvatar } from "@components_v2/avatar/random-colored-avatar";
import { StyledAutocompletePopper } from "@features/chip-popper-components/chip-types-input/chip-types-input.styled";

export const AutocompleteMultipleProjects = ({
    value,
    disabled,
    onFetchData,
    onInputChange,
    label,
    placeholder,
    limitTags,
    options,
    error,
    helperText,
    sx,
    handleClose,
    onChange,
    isPopperComponent = false,
    noOptionsText,
    onChangeSearchKeyword,
    ...rest
}) => {
    const [debouncedValue, setDebouncedValue] = useState(null); //디바운싱된 검색어
    const [searchedValue, setSearchedValue] = useState(null); //현재 검색중인 이름
    const [isLoading, setIsLoading] = useState(false);
    const observerRef = useRef(null);
    const targetElementRef = useRef(null);

    // 커스텀 setTarget 함수
    const setTarget = useCallback(
        (element) => {
            // 이전 observer 정리
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }

            targetElementRef.current = element;

            if (element) {
                // Popper 컨테이너 찾기
                const listboxContainer =
                    element.closest('[role="listbox"]') ||
                    element.closest(".MuiAutocomplete-listbox") ||
                    element.closest(".MuiPopper-root");

                // 새로운 observer 생성
                observerRef.current = new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            if (entry.isIntersecting && !isLoading) {
                                setIsLoading(true);
                                try {
                                    const result = onFetchData && onFetchData();
                                    if (result && typeof result.finally === "function") {
                                        result.finally(() => {
                                            setTimeout(() => setIsLoading(false), 300);
                                        });
                                    } else {
                                        setTimeout(() => setIsLoading(false), 300);
                                    }
                                } catch (error) {
                                    console.error("Error in onFetchData:", error);
                                    setTimeout(() => setIsLoading(false), 300);
                                }
                            }
                        });
                    },
                    {
                        root: listboxContainer, // Popper 컨테이너를 root로 설정
                        rootMargin: "200px 0px", // 위아래로 100px 여백
                        threshold: 0.5,
                    },
                );

                observerRef.current.observe(element);
            }
        },
        [onFetchData, isLoading],
    );

    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    const PopperComponent = useCallback((props) => {
        const { disablePortal, anchorEl, open, ...other } = props;
        return <StyledAutocompletePopper {...other}></StyledAutocompletePopper>;
    }, []);

    useEffect(() => {
        //실시간 검색 지연
        if (searchedValue === null) return;
        const debounce = setTimeout(() => {
            return setDebouncedValue(searchedValue);
        }, 300);
        return () => clearTimeout(debounce);
    }, [searchedValue]);

    useEffect(() => {
        if (debouncedValue === null) return;
        if (onChangeSearchKeyword) onChangeSearchKeyword(debouncedValue);
    }, [debouncedValue]);

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <MuiAutocomplete
                open={isPopperComponent ? true : undefined}
                PopperComponent={isPopperComponent ? PopperComponent : undefined}
                noOptionsText={noOptionsText}
                freeSolo={isPopperComponent}
                defaultValue={value || []}
                clearOnBlur
                multiple
                limitTags={limitTags ?? 2}
                onInputChange={(e, value, reason) => {
                    if (reason === "input") {
                        setSearchedValue(value);
                    } else if (reason === "clear" || reason === "reset") {
                        setSearchedValue(null);
                    }

                    onInputChange && onInputChange(reason);
                }}
                options={options}
                size={`small`}
                disabled={disabled}
                getOptionLabel={(option) => option.name}
                onChange={(e, newValue, reason) => {
                    if (onChange) {
                        // TextField 값 변경으로 인한 removeOption은 무시
                        if (reason === "removeOption" && e.type === "change") {
                            return;
                        }

                        if (reason === "removeOption" || reason === "selectOption") {
                            const clickedOption = options.find(
                                (opt) => opt.id === e.target.closest("li")?.getAttribute("data-option-id"),
                            );
                            if (clickedOption) {
                                if (value?.some((item) => item.id === clickedOption.id)) {
                                    const filteredValues = value.filter((item) => item.id !== clickedOption.id);
                                    onChange(filteredValues);
                                    return;
                                }
                            }
                        }

                        setSearchedValue(null);

                        const uniqueValues = newValue.filter(
                            (item, index, self) => index === self.findIndex((t) => t.id === item.id),
                        );
                        onChange(uniqueValues);
                    }
                }}
                disableCloseOnSelect
                renderTags={(v, getTagProps) => {
                    return value?.map((option, index) => {
                        return (
                            <Chip
                                key={index}
                                size={"small"}
                                label={option.name}
                                {...getTagProps({
                                    index,
                                })}
                            />
                        );
                    });
                }}
                renderOption={(props, option, { selected }) => {
                    const isSelected = selected || value?.some((el) => el.id === option.id);
                    return (
                        <ListItem
                            {...props}
                            aria-selected={isSelected}
                            key={option.id}
                            data-option-id={option.id}
                            ref={onFetchData && option.lastOption ? setTarget : null}
                        >
                            <Grid
                                container
                                flexDirection={"row"}
                                flexWrap={"nowrap"}
                                alignItems={"center"}
                                gap={1}
                                py={`1px`}
                            >
                                <Checkbox style={{ marginRight: 8 }} checked={isSelected} color={"greySecondary"} />
                                <Typography
                                    variant={"body1"}
                                    color={theme.palette.secondary[800]}
                                    sx={{ wordWrap: `break-word`, wordBreak: `keep-all` }}
                                >
                                    {`${option.name} (${option.id})`}
                                </Typography>
                            </Grid>
                        </ListItem>
                    );
                }}
                renderInput={(params) => {
                    const getEndAdornment = () => {
                        if (error === "warning") return <IconWarningTriangle color={"warning"} size={16} />;
                        else if (error === "error") return <IconErrorCircle color={`error`} size={16} />;

                        return undefined;
                    };
                    return (
                        <>
                            <TextField
                                {...params}
                                label={label}
                                placeholder={value && value.length > 0 ? "" : placeholder}
                                error={error}
                                inputProps={{
                                    ...params.inputProps,
                                }}
                                helperText={
                                    helperText && (
                                        <Grid container gap={1} alignItems={"center"}>
                                            {getEndAdornment()}
                                            {helperText}
                                        </Grid>
                                    )
                                }
                                sx={{
                                    ".MuiInputBase-root": {
                                        paddingTop: `3px !important`,
                                        minHeight: `40px`,
                                    },
                                    "&.MuiInputBase-input": {
                                        border: "none",
                                    },
                                    "&::placeholder": {
                                        color: theme.palette.secondary[400],
                                        fontWeight: "normal",
                                    },
                                    ...sx,
                                }}
                            />
                        </>
                    );
                }}
                {...rest}
            />
        </ClickAwayListener>
    );
};
